import { useCallback, useEffect, useRef, useState } from 'react';

// --- Configuration ---

/** Default turn duration in seconds (server-authoritative value) */
const DEFAULT_TURN_DURATION_SECONDS = 10;

/** Threshold in seconds at which the timer becomes "urgent" (R30) */
const URGENCY_THRESHOLD_SECONDS = 3;

/** Interval for updating the countdown display (ms) */
const TICK_INTERVAL_MS = 100;

// --- Hook Interface ---

export interface UseTimerOptions {
  /** Total turn duration in seconds (default: 10) */
  turnDuration?: number;
  /** Urgency threshold in seconds (default: 3) */
  urgencyThreshold?: number;
}

export interface UseTimerReturn {
  /** Seconds remaining on the countdown (clamped to 0) */
  secondsRemaining: number;
  /** Whether the timer is at or below the urgency threshold (≤3s) */
  isUrgent: boolean;
  /** Start the countdown from a server-provided turn_start ISO timestamp */
  startTimer: (turnStartIso: string) => void;
  /** Stop the countdown (freezes display at current value) */
  stopTimer: () => void;
  /** Reset the countdown to full turn duration (stopped state) */
  resetTimer: () => void;
}

// --- Hook Implementation ---

export function useTimer(options: UseTimerOptions = {}): UseTimerReturn {
  const {
    turnDuration = DEFAULT_TURN_DURATION_SECONDS,
    urgencyThreshold = URGENCY_THRESHOLD_SECONDS,
  } = options;

  const [secondsRemaining, setSecondsRemaining] = useState<number>(turnDuration);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const turnStartRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const turnDurationRef = useRef<number>(turnDuration);

  // Keep the ref in sync if turnDuration changes
  useEffect(() => {
    turnDurationRef.current = turnDuration;
  }, [turnDuration]);

  /** Calculate remaining seconds from turn start timestamp */
  const calculateRemaining = useCallback((): number => {
    if (turnStartRef.current === null) {
      return turnDurationRef.current;
    }
    const elapsed = (Date.now() - turnStartRef.current) / 1000;
    const remaining = turnDurationRef.current - elapsed;
    return Math.max(0, Math.round(remaining * 10) / 10);
  }, []);

  /** Clear the tick interval */
  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /** Start the countdown from a server-provided turn_start ISO timestamp */
  const startTimer = useCallback(
    (turnStartIso: string) => {
      const startTime = new Date(turnStartIso).getTime();
      turnStartRef.current = startTime;

      // Calculate initial value immediately
      const remaining = Math.max(
        0,
        Math.round((turnDurationRef.current - (Date.now() - startTime) / 1000) * 10) / 10
      );
      setSecondsRemaining(remaining);
      setIsRunning(true);

      // Clear any existing interval before starting a new one
      clearTick();

      intervalRef.current = setInterval(() => {
        const current = calculateRemaining();
        setSecondsRemaining(current);

        // Auto-stop when timer reaches 0
        if (current <= 0) {
          clearTick();
          setIsRunning(false);
        }
      }, TICK_INTERVAL_MS);
    },
    [calculateRemaining, clearTick]
  );

  /** Stop the countdown (freezes display at current value) */
  const stopTimer = useCallback(() => {
    clearTick();
    setIsRunning(false);
  }, [clearTick]);

  /** Reset the countdown to full turn duration (stopped state) */
  const resetTimer = useCallback(() => {
    clearTick();
    turnStartRef.current = null;
    setSecondsRemaining(turnDurationRef.current);
    setIsRunning(false);
  }, [clearTick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTick();
    };
  }, [clearTick]);

  // Derive urgency from remaining seconds
  const isUrgent = isRunning && secondsRemaining <= urgencyThreshold && secondsRemaining > 0;

  return {
    secondsRemaining,
    isUrgent,
    startTimer,
    stopTimer,
    resetTimer,
  };
}
